import {
  ExamSubtype,
  IOrganSegmentation,
  OrganSegmentationType
} from 'types/exam/types'

/**
 * Feature flag dos órgãos segmentados: ligada com qualquer valor em
 * `VITE_ENABLE_ORGAN_SEGMENTATION`, exceto ausente, vazio ou `false`.
 */
export const isOrganSegmentationEnabled = (): boolean => {
  const flag =
    import.meta.env.VITE_ENABLE_ORGAN_SEGMENTATION?.trim().toLowerCase()
  return Boolean(flag) && flag !== 'false'
}

/** Só exames AP/PA têm órgãos segmentados. */
export const isApPaSubtype = (subtype?: ExamSubtype | null): boolean =>
  subtype === 'cxray_ap' || subtype === 'cxray_pa'

interface IOrganConfig {
  label: string
  color: string
}

/**
 * Nome pt-BR e cor de destaque de cada órgão. As cores não vêm do
 * tema: são desenhadas sobre a radiografia (cinza), iguais nos modos claro e escuro, e precisam
 * de 14 tons distinguíveis entre si.
 */
export const ORGANS: Record<OrganSegmentationType, IOrganConfig> = {
  aorta: { label: 'Aorta', color: '#e6194b' },
  facies_diaphragmatica: { label: 'Diafragma', color: '#3cb44b' },
  heart: { label: 'Coração', color: '#ffe119' },
  left_clavicle: { label: 'Clavícula esquerda', color: '#4363d8' },
  right_clavicle: { label: 'Clavícula direita', color: '#f58231' },
  left_hilus_pulmonis: { label: 'Hilo pulmonar esquerdo', color: '#00e5a0' },
  right_hilus_pulmonis: { label: 'Hilo pulmonar direito', color: '#42d4f4' },
  left_lung: { label: 'Pulmão esquerdo', color: '#f032e6' },
  right_lung: { label: 'Pulmão direito', color: '#bfef45' },
  left_scapula: { label: 'Escápula esquerda', color: '#808000' },
  right_scapula: { label: 'Escápula direita', color: '#469990' },
  mediastinum: { label: 'Mediastino', color: '#000075' },
  spine: { label: 'Coluna', color: '#9a6324' },
  weasand: { label: 'Traqueia', color: '#800000' }
}

/** Fator sobre o alpha do PNG da máscara (que já vem semitransparente), não opacidade absoluta. */
export const ORGAN_OPACITY = { default: 0.5, min: 0.1, max: 1, step: 0.1 }

export const isKnownOrgan = (type: string): type is OrganSegmentationType =>
  Object.hasOwn(ORGANS, type)

export interface IKnownOrganSegmentation extends IOrganSegmentation {
  segmentation_type: OrganSegmentationType
}

/**
 * Mantém só os órgãos conhecidos (slug novo não vira checkbox em inglês), em ordem alfabética do
 * nome exibido no `locale` da interface.
 */
export const getKnownOrganSegmentations = (
  segmentations: IOrganSegmentation[] | undefined,
  locale = 'pt-BR'
): IKnownOrganSegmentation[] => {
  const collator = new Intl.Collator(locale)
  return (segmentations ?? [])
    .filter((segmentation): segmentation is IKnownOrganSegmentation =>
      isKnownOrgan(segmentation.segmentation_type)
    )
    .sort((a, b) =>
      collator.compare(
        ORGANS[a.segmentation_type].label,
        ORGANS[b.segmentation_type].label
      )
    )
}

/**
 * Quadrado da imagem original coberto pelo overlay, em pixels da original. Reproduz o
 * center-crop do sentinela_cxray_processor (`xray.rs::center_crop_square`), com as duas
 * divisões inteiras separadas.
 */
export const getOrganSegmentationRect = ({
  source_width,
  source_height
}: Pick<IOrganSegmentation, 'source_width' | 'source_height'>) => {
  const size = Math.min(source_width, source_height)
  return {
    x: Math.floor(source_width / 2) - Math.floor(size / 2),
    y: Math.floor(source_height / 2) - Math.floor(size / 2),
    size
  }
}
